import Logger from '@medflex/utils/Logger';
import env from '@medflex/utils/config';
import { getBrowser, getOS } from '@medflex/utils/getClientInfo';

export const CHAT_MODEL_NAME = '_chat';
let socketConnection;

const onError = event => {
  Logger.warn('[chat > socketConnection -> onError]', event);
};

const onOpen = event => {
  Logger.log('[chat > socketConnection -> onOpen]', event);
};

export default config => ({
  config: {
    models: {
      chat: {
        name: CHAT_MODEL_NAME,
        state: {
          connected: false,
        },
        reducers: {
          onClosed: () => ({ connected: false, closed: true }),
          onConnectionEstablished(state, { expiry }) {
            return { connected: true, expiresOn: expiry };
          },
          onConnectionFailed() {
            return { connected: false, failed: true };
          },
          onUserExpired() {
            return { connected: false, expired: true };
          },
        },
        effects(dispatch) {
          function onClose(event) {
            Logger.log('[chat > socketConnection -> onClose]', event);
            dispatch.errors.add({ realm: 'chat_connection', type: 'connection' });
            setTimeout(() => {
              dispatch[CHAT_MODEL_NAME].connect();
            }, 1000);
          }

          return {
            onMessage(event, state) {
              const { data } = event;
              Logger.log('[chat > socketConnection -> onMessage]', event);
              const message = JSON.parse(data);
              const { status, type } = message;
              if (type === 'login') {
                if (status === 'pending') {
                  dispatch.errors.add({ realm: 'chat_connection', type: 'connection' });
                  this.authenticate();
                }
                if (status === 'fail') {
                  dispatch.errors.add({ realm: 'chat_connection', type: 'connection' });
                  this.onConnectionFailed();
                }
                if (status === 'expired') {
                  dispatch.errors.add({ realm: 'chat_connection', type: 'connection' });
                  this.onUserExpired();
                }
                if (status === 'ok') {
                  dispatch.errors.remove({ realm: 'chat_connection', type: 'connection' });
                  this.onConnectionEstablished(message);
                }
                return;
              }

              dispatch.messageEvents.onMessage({ ...message, state });
            },
            authenticate(_, { oidcSession }) {
              if (!socketConnection) {
                Logger.log('chat::authenticate -> cannot send message, no open connection');
              } else {
                const jwt = oidcSession.access_token;
                if (jwt) {
                  try {
                    socketConnection.send(
                      JSON.stringify({
                        type: 'login',
                        jwt,
                        date: new Date().toJSON(),
                        app: `${env.VERSION}, ${env.CLIENT_TYPE}`,
                        userAgent: navigator && navigator.userAgent,
                        os: getOS() || undefined,
                        browser: getBrowser() || undefined,
                      }),
                    );
                  } catch (e) {
                    Logger.log('chat::authenticate -> socket connection failed', e);
                  }
                }
              }
            },
            connect(_, { oidcSession, _chat }) {
              if (socketConnection && socketConnection.readyState === 1) {
                Logger.log('chat::connect -> socket connection found, will close it');
                socketConnection.removeEventListener('open', onOpen);
                socketConnection.removeEventListener('message', this.onMessage);
                socketConnection.removeEventListener('error', onError);
                socketConnection.removeEventListener('close', onClose);
                socketConnection.close();
              }
              // prevent reconnection attempt if either oidc or websocket reports expired token
              // or if websocket reports invalid login attempt
              if (oidcSession.expired || _chat.expired) {
                Logger.log('chat::connect -> session expired, cannot connect');
              } else if (_chat.failed) {
                Logger.log('chat::connect -> websocket reported invalid login, stop attempting');
              } else {
                socketConnection = new WebSocket(config.url, config.protocols);
                socketConnection.addEventListener('open', onOpen);
                socketConnection.addEventListener('message', this.onMessage);
                socketConnection.addEventListener('error', onError);
                socketConnection.addEventListener('close', onClose);
              }
            },
            disconnect() {
              if (!socketConnection) {
                Logger.log('chat::disconnet -> missing socket connection');
              } else {
                socketConnection.removeEventListener('open', onOpen);
                socketConnection.removeEventListener('message', this.onMessage);
                socketConnection.removeEventListener('error', onError);
                socketConnection.removeEventListener('close', onClose);
                socketConnection.close();
                dispatch[CHAT_MODEL_NAME].onClosed();
              }
            },
          };
        },
      },
    },
  },
  createMiddleware() {
    return () => next => action => {
      if (action.type === 'system/onPageVisibilityChanged') {
        const { payload } = action;
        if (payload.visibilityState === 'hidden') {
          this.dispatch[CHAT_MODEL_NAME].disconnect();
        } else {
          this.dispatch[CHAT_MODEL_NAME].connect();
        }
      }
      return next(action);
    };
  },
});
